import React from "react";

/**
 * Generate mapper functions, that returns checkbox like { item: 1, checked: [true/false] } instead just item (1)
 */
const mapToCheckArray = value => item => ({
	item,
	selected: value
});

/**
 * Returns only checked items from checkbox array with priority "higher index - upper priority"
 */
const filterChecked = checkArray => {
	const result = [];

	checkArray.forEach(({ item, selected }) => {
		const existsIndex = result.findIndex(
			v => JSON.stringify(v.item) === JSON.stringify(item)
		);

		if (existsIndex !== -1) {
			result[existsIndex] = { item, selected };
		} else {
			result.push({ item, selected });
		}
	});

	return result.filter(({ selected }) => selected).map(({ item }) => item);
};

/**
 * Returns selection with added items
 */
const addItemsToSelection = (selection, items) => {
	const selected = selection.map(mapToCheckArray(true));
	const itemsToAdd = items.map(mapToCheckArray(true));

	return filterChecked([...selected, ...itemsToAdd]);
};

/**
 * Returns selection with removed items
 */
const removeItemsFromSelection = (selection, items) => {
	const selected = selection.map(mapToCheckArray(true));
	const itemsToRemove = items.map(mapToCheckArray(false));

	return filterChecked([...selected, ...itemsToRemove]);
};

const useRowSelection = (allItems, selectedItems, onChangeSelection) => {
	const isSelectedAll = React.useMemo(() => {
		return (
			allItems.filter(item => !selectedItems.includes(item)).length === 0
		);
	}, [selectedItems, allItems]);

	const checkIsSelected = React.useCallback(
		key => selectedItems.includes(key),
		[selectedItems]
	);

	const toggleSelection = React.useCallback(
		key => {
			if (checkIsSelected(key)) {
				onChangeSelection(
					removeItemsFromSelection(selectedItems, [key])
				);
			} else {
				onChangeSelection(addItemsToSelection(selectedItems, [key]));
			}
		},
		[selectedItems, checkIsSelected, onChangeSelection]
	);
	const toggleSelectionAll = React.useCallback(() => {
		onChangeSelection(
			isSelectedAll
				? removeItemsFromSelection(selectedItems, allItems)
				: addItemsToSelection(selectedItems, allItems)
		);
	}, [allItems, isSelectedAll, onChangeSelection, selectedItems]);

	return {
		checkIsSelected,
		isSelectedAll,
		toggleSelection,
		toggleSelectionAll
	};
};

export default useRowSelection;
